import * as R from 'ramda'
import {
  byPosition,
  sortChampionshipResultsByPerformance,
  sortPlayersByPerformance,
} from './compare'

import { GlobalTeam, TeamId } from 'src/schemas/Team'
import { CategoryId, TournamentCategory } from '../schemas/Category'
import { Championship } from '../schemas/Championship'
import { ChampionshipCategory } from '../schemas/ChampionshipCategory'
import { ChampionshipResults, ChampionshipResultsWithTotals } from '../schemas/ChampionshipResults'
import { Flight } from '../schemas/Flight'
import { PlayerId, TournamentPlayer } from '../schemas/Player'
import { Playground } from '../schemas/Playground'
import {
  CombinedResults,
  CombinedResultsGroup,
  FlightResults,
  PlayerRoundResult,
  PlayerStatistics,
} from '../schemas/Results'
import { Tournament, TournamentId } from '../schemas/Tournament'
import isDefined from './isDefined'

export const resultKey = (playerId, round) => `${playerId}_${round}`

const average = (totalScore, length) =>
  length ? Math.round((totalScore * 100.0) / length) / 100 : null

export const flightResults = (
  flight,
  tournamentPlayers,
  tournamentResults,
  holes,
): FlightResults => {
  const { players, round } = flight
  const result = players.reduce(
    (acc, playerId) => {
      const compositKey = resultKey(playerId, round)
      const player = tournamentPlayers.find((obj) => obj.id === playerId)
      const playerResult = tournamentResults.find((obj) => obj.id === compositKey)
      const tmpPlayerResults = playerResult
        ? playerResult.results
        : Array.from({ length: holes }).map((x, i) => 0)

      return {
        orderInRound: acc.orderInRound + 1,
        players: {
          [compositKey]: {
            playerId: player.id,
            round,
            name: `${player.firstName} ${player.lastName}`,
            results: tmpPlayerResults,
            orderInRound: acc.orderInRound,
          },
          ...acc.players,
        },
      }
    },
    { orderInRound: 0, players: {} },
  )

  return result.players
}

export const playerResults = (
  playground: Playground,
  rounds: number,
  player: TournamentPlayer,
  tournamentResultsMap: { [id: string]: PlayerRoundResult },
  tournamentCombinedResultsMap: { [id: string]: CombinedResults },
) => {
  // tslint:disable-next-line: no-unnecessary-type-assertion
  const selectedResults = R.path([player.id, 'withFinal'], tournamentCombinedResultsMap) as FIXME

  if (R.isNil(selectedResults)) {
    return {}
  }

  const roundsArray = R.range(1, rounds + 1)

  const sumRow = roundsArray.reduce(
    (acc, round) => ({
      ...acc,
      [round]: R.path(['results', round, 'score'], selectedResults),
    }),
    { key: 'SUM' },
  )

  const perRoundLaneResults = roundsArray.map((round) =>
    R.pathOr(
      R.repeat(undefined, playground.holes),
      [resultKey(player.id, round), 'results'],
      tournamentResultsMap,
    ),
  )

  const perLaneRows = R.transpose(perRoundLaneResults).map((laneResults, laneIndex) => {
    const playedLaneResults = R.takeWhile<FIXME>((score) => Boolean(score), laneResults)
    const playedLaneResultsSum = R.sum(playedLaneResults)

    return laneResults.reduce(
      (acc: FIXME, singleResult: FIXME, index) => ({
        ...acc,
        [index + 1]: singleResult > 0 ? singleResult : undefined,
      }),
      {
        key: laneIndex + 1,
        avgScore:
          playedLaneResultsSum > 0
            ? average(playedLaneResultsSum, playedLaneResults.length)
            : undefined,
      },
    )
  })

  const holeResults = [...perLaneRows, sumRow]

  const statistics: PlayerStatistics = [
    { key: 'Penalties', value: player.penalties || 0 },
    { key: 'SUM', value: selectedResults.totalScore },
    { key: 'Average', value: selectedResults.avgScore },
    { key: 'Different', value: selectedResults.diff },
    { key: 'PAR', value: selectedResults.parDiff },
    { key: 'Aces', value: selectedResults.totalAces },
    { key: 'Aces In Row', value: selectedResults.maxAcesInRow },
  ]

  return { holeResults, statistics }
}

const playerPlayoffRank = (categoryId: CategoryId) => R.path(['lastRoundPlayoffRank', categoryId])

export const playsFinals = (playerId: PlayerId, flights: Flight[]) =>
  R.any((flight: FIXME) => flight.isFinal && R.contains(playerId, flight.players), flights)

const groupActiveByPar = (arrayWithPar) =>
  R.groupWith(
    (left: FIXME, right) =>
      left.totalScore > 0 && right.totalScore > 0 && left.parDiff === right.parDiff,
    arrayWithPar,
  )

const groupByRank = (arrayWithRank, categoryId: CategoryId, orderByDifferent: boolean) =>
  R.groupWith(
    (left: FIXME, right) =>
      playerPlayoffRank(categoryId)(left) === playerPlayoffRank(categoryId)(right) &&
      (orderByDifferent ? left.diff === right.diff : true),
    arrayWithRank,
  )

// TODO test
export const rankRecords = <T extends object>(startIndex: number, groupedByRank: T[][]) => {
  const { processedByRankRecords } = groupedByRank.reduce(
    (rankAcc, sameRankRecords) => ({
      rankIndex: rankAcc.rankIndex + sameRankRecords.length,
      processedByRankRecords: R.concat(
        rankAcc.processedByRankRecords,
        sameRankRecords.map(
          R.assoc(
            'rank',
            sameRankRecords.length > 1
              ? `${startIndex + rankAcc.rankIndex + 1}. - ${
                  startIndex + rankAcc.rankIndex + sameRankRecords.length
                }.`
              : `${startIndex + rankAcc.rankIndex + 1}.`,
          ),
        ),
      ),
    }),
    { rankIndex: 0, processedByRankRecords: [] },
  )

  return processedByRankRecords
}

const combinedResultGroupWithPlayer = (
  player: TournamentPlayer,
  combinedResultGroup: CombinedResultsGroup,
) => {
  const { penalties = 0 } = player
  const { totalScore, parDiff = 0 } = combinedResultGroup

  return {
    ...player,
    ...combinedResultGroup,
    totalScore: totalScore + penalties,
    parDiff: parDiff + penalties,
    penalties,
  }
}

export const categoryResults = (
  tournament: Tournament,
  category: TournamentCategory,
  tournamentFlights: Flight[],
  tournamentCombinedResults: CombinedResults[],
  tournamentPlayersMap: { [id: string]: TournamentPlayer },
) => {
  const orderByDifferent = tournament.orderByDifferent
  const withFinal = tournament.finalRound && (tournament.categoriesCloseAfterFinal || category.main)

  const playersScore = tournamentCombinedResults
    .map((combinedResult) => {
      const selected = withFinal ? combinedResult.withFinal : combinedResult.withoutFinal
      const player = tournamentPlayersMap[combinedResult.id]

      if (R.isNil(player)) {
        return undefined
      }

      return {
        ...combinedResultGroupWithPlayer(player, selected),
        playsFinals:
          withFinal &&
          // enabling this causes players that have results in final round to be sorted before those who dont
          // R.has(`${tournament.rounds + 1}`, combinedResult.withFinal.results) &&
          playsFinals(combinedResult.id, tournamentFlights),
        played: R.pathOr(0, ['results', '1', 'score'], selected) > 0,
      }
    })
    .filter(isDefined)

  const sortedPlayersScore = sortPlayersByPerformance(category.id)(playersScore)

  const groupedByPar = groupActiveByPar(sortedPlayersScore)

  const { sortedPlayers } = groupedByPar.reduce(
    (acc, sharedRankPlayers) => {
      const groupedByRank = groupByRank(sharedRankPlayers, category.id, orderByDifferent)

      const processedByRankPlayers = rankRecords(acc.index, groupedByRank)

      return {
        index: acc.index + processedByRankPlayers.length,
        sortedPlayers: R.concat(
          acc.sortedPlayers,
          processedByRankPlayers.map(
            R.assoc('playoffRankPlayers', sharedRankPlayers.map(R.prop('id'))),
          ),
        ),
      }
    },
    {
      index: 0,
      sortedPlayers: [],
    },
  )

  return sortedPlayers
}

/**
 * Adds zeros to tournaments where player didn't play
 *
 * Sorts tournaments by points and dateFrom
 *
 * @param championshipTournaments
 * @param categoryPoints
 * @returns
 */
const normalizeChampionshipResultsInCategory = (
  championshipTournaments: Tournament[],
  categoryPoints: {
    [tournamentId: string]: number
  },
): [
  {
    [tournamentId: string]: number
  },
  TournamentId[],
] => {
  const normalizedPoints = championshipTournaments.reduce(
    (acc, tournament) => ({
      ...acc,
      [tournament.id]: {
        tournament,
        total: tournament.id in categoryPoints ? categoryPoints[tournament.id] : 0,
      },
    }),
    {} as { [tournamentId: string]: { tournament: Tournament; total: number } },
  )
  const sortedTournaments: TournamentId[] = R.sortWith(
    [R.ascend(R.prop('total')), R.ascend(R.path(['tournament', 'dateFrom']))],
    Object.values(normalizedPoints),
  ).map(R.pathOr('', ['tournament', 'id']))

  return [R.mapObjIndexed((value) => value.total, normalizedPoints), sortedTournaments]
}

/**
 * calculate result totals in all categories
 *
 * drops results based on championship configuration
 *
 * @param championship
 * @param championshipTournaments
 * @param points
 * @returns
 */
const calculateChampionshipResultTotals = (
  championship: Championship,
  championshipTournaments: Tournament[],
  points: {
    [categoryId: string]: { [tournamentId: string]: number }
  },
): [{ [categoryId: string]: number }, { [categoryId: string]: TournamentId[] }] =>
  Object.entries(points).reduce(
    ([acc, accSortedTournaments], [categoryId, resultsInCategory]) => {
      const [normalizedPoints, sortedTournaments] = normalizeChampionshipResultsInCategory(
        championshipTournaments,
        resultsInCategory,
      )

      const totals = R.sum(
        Object.values(
          R.omit(
            R.take(championship.dropTournamentsCount ?? 0, sortedTournaments),
            normalizedPoints,
          ),
        ),
      )

      return [
        {
          ...acc,
          [categoryId]: totals,
        },
        {
          ...accSortedTournaments,
          [categoryId]: sortedTournaments,
        },
      ]
    },
    [{}, {}] as [{ [categoryId: string]: number }, { [categoryId: string]: TournamentId[] }],
  )

export const championshipCategoryResults = (
  championship: Championship,
  championshipTournaments: Tournament[],
  category: ChampionshipCategory,
  results: ChampionshipResults[],
) => {
  const categoryId = category.id

  const resultsWithTotals: ChampionshipResultsWithTotals[] = results.map((result) => {
    const [totals, sortedTournaments] = calculateChampionshipResultTotals(
      championship,
      championshipTournaments,
      result.points,
    )

    return {
      ...result,
      totals,
      sortedTournaments,
    } as ChampionshipResultsWithTotals
  })

  const sortedResults = sortChampionshipResultsByPerformance(category.id)(resultsWithTotals)

  const groupedByTotal = R.groupWith(
    (left: ChampionshipResultsWithTotals, right) =>
      left.totals[categoryId] === right.totals[categoryId],
    sortedResults,
  )

  const { sortedResultsWithRank } = groupedByTotal.reduce(
    (acc, sharedRankResults) => {
      const processedByRankPlayers = rankRecords(acc.index, [sharedRankResults])

      return {
        index: acc.index + processedByRankPlayers.length,
        sortedResultsWithRank: R.concat(
          acc.sortedResultsWithRank,
          processedByRankPlayers.map(
            R.assoc('playoffRankPlayers', sharedRankResults.map(R.prop('id'))),
          ),
        ),
      }
    },
    {
      index: 0,
      sortedResultsWithRank: [],
    },
  )

  return sortedResultsWithRank
}

const CATEGORY_TEAMS = 'teams'

export const teamResults = (
  tournament: Tournament,
  tournamentPlayers: TournamentPlayer[],
  tournamentCombinedResultsMap: { [id: string]: CombinedResults },
  globalTeams: Record<TeamId, GlobalTeam>,
  tournamentTeamsMap: any,
) => {
  const orderByDifferent = tournament.orderByDifferent

  const { teams, sameNationalityTeamIds: nationalityTeamIds } = tournamentPlayers.reduce(
    (teamsAcc, player) => {
      const combinedResults = tournamentCombinedResultsMap[player.id] as CombinedResults | undefined

      if (R.isNil(player.team) || R.isNil(player.id) || R.isNil(combinedResults)) {
        return teamsAcc
      }

      const teamId = player.team.id
      const team = teamsAcc.teams[teamId]
      const existingPlayers = team ? team.players : {}

      const sameNationalityTeamIds = teamsAcc.sameNationalityTeamIds[player.nationality]
      const newSameNationalityTeamIds = sameNationalityTeamIds
        ? [...sameNationalityTeamIds, teamId].sort()
        : [teamId]

      const selectedResults = combinedResults.withoutFinal

      // team exist - means we are processing other players - no impact on the teamName

      return {
        teams: {
          ...teamsAcc.teams,
          [teamId]: {
            id: teamId,
            players: {
              ...existingPlayers,
              [player.id]: {
                ...combinedResultGroupWithPlayer(player, selectedResults),
              },
            },
            nationality: team ? team.nationality : player.nationality,
            isDisqualified: (team ? team.isDisqualified : false) || player.isDisqualified,
            score: (team ? team.score : 0) + selectedResults.totalScore,
            penalties: (team ? team.penalties : 0) + player.penalties,
            parDiff: (team ? team.parDiff : 0) + selectedResults.parDiff || 0,
            lastRoundPlayoffFlag:
              tournamentTeamsMap &&
              tournamentTeamsMap[teamId] &&
              tournamentTeamsMap[teamId].playoffFlag,
            lastRoundPlayoffRank:
              tournamentTeamsMap &&
              tournamentTeamsMap[teamId] &&
              tournamentTeamsMap[teamId].playoffRank,
          },
        },
        sameNationalityTeamIds: {
          ...teamsAcc.sameNationalityTeamIds,
          [player.nationality]: team ? sameNationalityTeamIds : newSameNationalityTeamIds,
        },
      }
    },
    { teams: {}, sameNationalityTeamIds: {} },
  )

  // drops the last attribute which is the key sameNationalityTeamIds
  const groupedTeamsByPar = groupActiveByPar(
    sortPlayersByPerformance(CATEGORY_TEAMS)(R.values(teams)),
  )

  const { sortedTeams } = groupedTeamsByPar.reduce(
    (acc, resultsGroup) => {
      const groupedByRank = groupByRank(resultsGroup, CATEGORY_TEAMS, orderByDifferent)
      // TODO check if groupByRank could be used here
      const { processedByRankTeams } = groupedByRank.reduce(
        (rankAcc, sameRankTeams) => ({
          rankIndex: rankAcc.rankIndex + sameRankTeams.length,
          processedByRankTeams: R.concat(
            rankAcc.processedByRankTeams,
            sameRankTeams.map((teamResult) => ({
              ...teamResult,
              players: R.sortWith([byPosition])(R.values(teamResult.players)),
              playoffRankTeams: sameRankTeams.map((team) => team.id),
              rank:
                sameRankTeams.length > 1
                  ? `${acc.index + rankAcc.rankIndex + 1}. - ${
                      acc.index + rankAcc.rankIndex + sameRankTeams.length
                    }.`
                  : `${acc.index + rankAcc.rankIndex + 1}.`,
              teamName: globalTeams[teamResult.id]
                ? globalTeams[teamResult.id].name
                : `${teamResult.nationality}-${
                    R.indexOf(teamResult.id, nationalityTeamIds[teamResult.nationality]) + 1
                  }`,
            })),
          ),
        }),
        { rankIndex: 0, processedByRankTeams: [] },
      )

      return {
        index: acc.index + processedByRankTeams.length,
        sortedTeams: R.concat(
          acc.sortedTeams,
          processedByRankTeams.map(R.assoc('playoffRankTeams', resultsGroup.map(R.prop('id')))),
        ),
      }
    },
    {
      index: 0,
      sortedTeams: [],
    },
  )

  return sortedTeams
}

export const playersFromFlight = (flight, tournamentPlayers) =>
  flight.players.map((id, index) => tournamentPlayers[id])



// WEBPACK FOOTER //
// ./src/helpers/results.ts